import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { BellRing, CheckCircle2, FilePlus2, Lock, Search } from "lucide-react";

import { disciplineService, type BehaviorCategory, type BehaviorIncident, type DisciplinaryAction, type StudentOption } from "../../services/disciplineService";
import { apiErrorMessage } from "../../utils/apiError";
import { nowLocalInput, todayIso } from "../../utils/dates";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ReasonPanel, errorLine } from "../inventory/InventoryParts";
import { SearchPick } from "../library/Pickers";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { approvalLabel, awaitsApproval, day, incidentConfig, incidentStatus, isOpenCase, nextStep, resolveBlockers, severityLabel, when } from "./disciplineLogic";

/** Reporting a conduct incident: the facts, and the action proposed. */
function IncidentForm({ learners, categories, sanctions, onClose, onDone }: {
  learners: StudentOption[];
  categories: BehaviorCategory[];
  sanctions: DisciplinaryAction[];
  onClose: () => void;
  onDone: (row: BehaviorIncident) => void;
}) {
  const [learner, setLearner] = useState("");
  const [category, setCategory] = useState("");
  const [sanction, setSanction] = useState("");
  const [happened, setHappened] = useState(nowLocalInput());
  const [location, setLocation] = useState("");
  const [account, setAccount] = useState("");
  const save = useMutation({ mutationFn: disciplineService.createBehaviorIncident, onSuccess: onDone });
  const chosen = sanctions.find((row) => String(row.id) === sanction);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      student: Number(learner),
      title: String(data.get("title") ?? "").trim(),
      category: Number(category),
      action_taken: Number(sanction),
      incident_date: new Date(happened).toISOString(),
      location: location.trim(),
      witnesses: String(data.get("witnesses") ?? "").trim(),
      description: account.trim(),
      is_confidential: data.get("is_confidential") === "on",
    });
  };
  const sorted = [...learners].sort((a, b) => (a.class_level_order ?? 999) - (b.class_level_order ?? 999) || a.full_name.localeCompare(b.full_name));
  return (
    <FormModal
      id="conduct-incident"
      title="Report an incident"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The incident could not be reported.") : undefined}
      saveLabel="Report it"
      note="The committee investigates; you can follow the case here."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Who and when" divided={false}>
        <Field label="Learner" as="div" className="sm:col-span-2">
          <SearchPick required items={sorted} value={learner} onChange={setLearner} label={(row) => row.full_name} detail={(row) => [row.class_name, row.student_id].filter(Boolean).join(" · ")} placeholder="Search by name, class or learner number" empty="No learner matches" />
        </Field>
        <Field label="When it happened">
          <input required type="datetime-local" max={nowLocalInput()} value={happened} onChange={(e) => setHappened(e.target.value)} className="input mt-1" />
        </Field>
        <Field label="Where" optional hint="e.g. P5 classroom, the dining hall, the school gate.">
          <input maxLength={200} value={location} onChange={(e) => setLocation(e.target.value)} className="input mt-1" />
        </Field>
      </FormSection>

      <FormSection title="What happened">
        <Field label="In a few words" className="sm:col-span-2" hint="A neutral heading, e.g. Pushing at the tap stand.">
          <input required name="title" maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Kind of behaviour">
          <select required value={category} onChange={(e) => setCategory(e.target.value)} className="input mt-1">
            <option value="">Choose one</option>
            {categories.filter((row) => row.is_active).map((row) => <option key={row.id} value={row.id}>{row.name} · {severityLabel(row.severity_level)}</option>)}
          </select>
        </Field>
        <Field label="Action proposed" hint={chosen ? [chosen.requires_principal_approval || chosen.severity_level >= 4 ? "Leadership must approve it." : "", chosen.requires_parent_notification ? "The parent is told." : ""].filter(Boolean).join(" ") || undefined : undefined}>
          <select required value={sanction} onChange={(e) => setSanction(e.target.value)} className="input mt-1">
            <option value="">Choose one</option>
            {sanctions.filter((row) => row.is_active).map((row) => <option key={row.id} value={row.id}>{row.name}{row.duration_days ? ` · ${row.duration_days} days` : ""}</option>)}
          </select>
        </Field>
        <Field label="Account" as="div" className="sm:col-span-2" hint="The facts only: what was seen and heard, not opinions about the learner.">
          <AIAssistField
            rows={4}
            value={account}
            onChange={(next) => setAccount(next)}
            placeholder="e.g. At 10:30, during break, the learner pushed another learner at the tap stand. Both were separated by the duty teacher."
            context={{ key: "discipline.note", params: { kind: "incident", category: category || undefined, action_taken: sanction || undefined, incident_date: happened, location: location || undefined } }}
            draftLabel="Write it up"
            draftHint="Jot what you saw first; it writes a neutral account and adds nothing you did not say."
          />
        </Field>
        <Field label="Witnesses" optional className="sm:col-span-2">
          <input name="witnesses" className="input mt-1" />
        </Field>
        <label className="flex items-start gap-2 text-sm text-gray-700 sm:col-span-2">
          <input type="checkbox" name="is_confidential" defaultChecked className="mt-0.5 h-4 w-4 rounded border-gray-300" />
          <span>
            <span className="font-medium">Confidential</span>
            <span className="block text-xs text-gray-500">Only you, the disciplinary committee and school leadership see it; not the learner's class teacher.</span>
          </span>
        </label>
      </FormSection>
    </FormModal>
  );
}

/** Resolving a case: the action taken, how long it lasts and any follow-up. */
function ResolvePanel({ incident, busy, error, onSubmit }: { incident: BehaviorIncident; busy: boolean; error: unknown; onSubmit: (data: Record<string, unknown>) => void }) {
  const [notes, setNotes] = useState("");
  const [days, setDays] = useState(String(incident.action_duration ?? 0));
  const [followUp, setFollowUp] = useState(false);
  const [followDate, setFollowDate] = useState("");
  const [followNotes, setFollowNotes] = useState("");
  const blockers = resolveBlockers(incident);
  return (
    <ActionPanel title="Resolve the case" description="Record the action taken. Resolved cases feed the learner's behaviour pattern.">
      {blockers.length > 0 && (
        <ul className="list-disc space-y-0.5 rounded-md bg-amber-50 py-2 pl-7 pr-3 text-sm text-amber-900">
          {blockers.map((text) => <li key={text}>{text}</li>)}
        </ul>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Action taken" as="div" className="sm:col-span-2">
          <AIAssistField
            rows={2}
            value={notes}
            onChange={(next) => setNotes(next)}
            placeholder={`e.g. ${incident.action_taken_name} given; restorative talk with both learners.`}
            context={{ key: "discipline.note", params: { kind: "resolve", id: incident.id } }}
            draftLabel="Draft it"
            draftHint="Jot what was done first; it adds nothing you did not say."
          />
        </Field>
        <Field label="Days it lasts" hint="0 when it is not timed.">
          <input type="number" min={0} value={days} onChange={(e) => setDays(e.target.value)} className="input mt-1" />
        </Field>
        <label className="flex items-center gap-2 self-end pb-2 text-sm text-gray-700">
          <input type="checkbox" checked={followUp} onChange={(e) => setFollowUp(e.target.checked)} className="h-4 w-4 rounded border-gray-300" /> Follow up later
        </label>
        {followUp && (
          <>
            <Field label="Follow up on">
              <input required type="date" min={todayIso()} value={followDate} onChange={(e) => setFollowDate(e.target.value)} className="input mt-1" />
            </Field>
            <Field label="What to check" optional>
              <input value={followNotes} onChange={(e) => setFollowNotes(e.target.value)} className="input mt-1" />
            </Field>
          </>
        )}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={busy || blockers.length > 0 || notes.trim().length < 5 || (followUp && !followDate)}
          onClick={() => onSubmit({ action_notes: notes.trim(), action_duration: Number(days) || 0, follow_up_required: followUp, follow_up_date: followUp ? followDate : undefined, follow_up_notes: followUp ? followNotes.trim() : "" })}
          className="btn btn-primary gap-1.5"
        >
          <CheckCircle2 className="h-4 w-4" /> {busy ? "Saving…" : "Resolve"}
        </button>
      </div>
      {Boolean(error) && errorLine(error, "The case could not be resolved.")}
    </ActionPanel>
  );
}

/** Conduct cases: reporting, investigating, deciding, telling the parent and closing. */
export function IncidentsPanel({ incidents, learners, categories, sanctions, isLoading, isError, onRetry, me, isReviewer, isLeadership, onChanged, initialView }: {
  incidents?: BehaviorIncident[];
  learners: StudentOption[];
  categories: BehaviorCategory[];
  sanctions: DisciplinaryAction[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  me?: number;
  isReviewer: boolean;
  isLeadership: boolean;
  onChanged: () => void;
  initialView?: string;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [reporting, setReporting] = useState(false);
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const investigate = useMutation({ mutationFn: disciplineService.investigateIncident, onSuccess: () => done("Investigation opened.") });
  const escalate = useMutation({ mutationFn: disciplineService.escalateIncident, onSuccess: () => done("The case was escalated to the committee and leadership.") });
  const approve = useMutation({ mutationFn: disciplineService.approveIncident, onSuccess: () => done("The proposed action is approved.") });
  const reject = useMutation({ mutationFn: disciplineService.rejectIncidentApproval, onSuccess: () => done("The proposed action was rejected; the committee will decide again.") });
  const notify = useMutation({ mutationFn: disciplineService.notifyParent, onSuccess: (result) => done(`${result.notified_count} parent account${result.notified_count === 1 ? "" : "s"} told to contact the school.`) });
  const response = useMutation({ mutationFn: disciplineService.recordParentResponse, onSuccess: () => done("The parent's response is on the record.") });
  const resolve = useMutation({ mutationFn: disciplineService.resolveIncident, onSuccess: () => done("The case is resolved. Close it once any follow-up is done.") });
  const close = useMutation({ mutationFn: disciplineService.closeIncident, onSuccess: () => done("The case is closed.") });
  const actions = [investigate, escalate, approve, reject, notify, response, resolve, close];
  const setUp = categories.some((row) => row.is_active) && sanctions.some((row) => row.is_active);

  const columns: RegisterColumn<BehaviorIncident>[] = [
    { header: "Date", cell: (row) => day(row.incident_date), exportValue: (row) => when(row.incident_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    {
      header: "Learner",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          {row.student_class && <span className="block text-xs text-gray-500">{row.student_class}</span>}
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[10rem]",
    },
    { header: "Class", cell: (row) => row.student_class ?? "", exportValue: (row) => row.student_class ?? "", exportOnly: true },
    {
      header: "Incident",
      cell: (row) => (
        <span className="flex items-center gap-1.5">
          {row.is_confidential && <Lock className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-label="Confidential" />}
          <span className="text-gray-900">{row.title}</span>
        </span>
      ),
      exportValue: (row) => row.title,
      className: "min-w-[12rem]",
    },
    {
      header: "Behaviour",
      cell: (row) => (
        <span className="flex items-center gap-1.5 text-gray-700">
          <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: row.category_color }} aria-hidden />
          {row.category_name}
        </span>
      ),
      exportValue: (row) => row.category_name,
    },
    { header: "Severity", cell: (row) => <span className={row.severity_level >= 4 ? "font-medium text-red-700" : "text-gray-700"}>{severityLabel(row.severity_level)}</span>, exportValue: (row) => severityLabel(row.severity_level), className: "whitespace-nowrap" },
    { header: "Action proposed", cell: (row) => row.action_taken_name, exportValue: (row) => row.action_taken_name, className: "text-gray-700" },
    { header: "Next step", cell: (row) => <span className={isOpenCase(row) ? "text-gray-900" : "text-gray-400"}>{nextStep(row)}</span>, exportValue: (row) => nextStep(row), className: "whitespace-nowrap" },
    { header: "Status", cell: (row) => <StatusDot {...incidentStatus(row)} />, exportValue: (row) => incidentStatus(row).label },
    { header: "Reported by", cell: (row) => row.reported_by_name, exportValue: (row) => row.reported_by_name, exportOnly: true },
    { header: "Where", cell: (row) => row.location, exportValue: (row) => row.location, exportOnly: true },
    { header: "Approval", cell: (row) => approvalLabel(row), exportValue: (row) => approvalLabel(row), exportOnly: true },
  ];

  const incident = viewing !== null ? incidents?.find((row) => row.id === viewing) : undefined;
  const open = incident ? isOpenCase(incident) : false;
  const ownCase = incident?.reported_by === me;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        title="Incidents"
        description={isReviewer ? "Open a case to investigate it, decide it, tell the parent and close it." : "Cases you reported, and those about learners in your class. Open one to follow it."}
        actions={
          <button type="button" disabled={!setUp} title={setUp ? undefined : "Leadership must set up behaviour kinds and actions first (Definitions)."} onClick={() => setReporting(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <FilePlus2 className="mr-1.5 h-3.5 w-3.5" /> Report an incident
          </button>
        }
        rows={incidents}
        config={incidentConfig(me)}
        initialState={initialView ? { view: initialView } : undefined}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          actions.forEach((mutation) => mutation.reset());
          setViewing(row.id);
        }}
        noun={["incident", "incidents"]}
        exportName={{ file: "conduct-incidents", title: "Conduct Incidents" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={isReviewer ? "No incidents reported." : "No incidents you can see: those you report, and those about learners in your class, appear here."}
      />

      {incident && (
        <DetailsModal
          size="3xl"
          title={incident.title}
          headline={{ label: [incident.student_name, incident.student_class, incident.student_id].filter(Boolean).join(" · "), value: incident.category_name, note: `Severity ${severityLabel(incident.severity_level)} · proposed: ${incident.action_taken_name}` }}
          status={incidentStatus(incident)}
          notice={
            incident.is_confidential ? (
              <p className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700"><Lock className="h-4 w-4" /> Confidential: not shown to the learner's class teacher.</p>
            ) : undefined
          }
          facts={[
            ["When", when(incident.incident_date)],
            ["Where", incident.location],
            ["Reported by", `${incident.reported_by_name} on ${day(incident.created_at)}`],
            ["Investigated by", incident.investigated_by_name ? `${incident.investigated_by_name}, ${day(incident.investigation_started_at)}` : undefined],
            ["Leadership approval", `${approvalLabel(incident)}${incident.approved_by_name ? ` by ${incident.approved_by_name}` : incident.approval_rejected_by_name ? ` by ${incident.approval_rejected_by_name}` : ""}`],
            ["Parent", incident.parent_notified ? `Told ${day(incident.parent_notification_date)} by ${incident.parent_notified_by_name}` : incident.action_needs_parent_notice ? "Not told yet" : "Not needed"],
            ["Resolved", incident.resolved_by_name ? `${incident.resolved_by_name}, ${day(incident.resolved_at)}${incident.action_duration ? ` · lasts ${incident.action_duration} days` : ""}` : undefined],
            ["Follow up", incident.follow_up_required ? day(incident.follow_up_date) : undefined],
            ["Closed", incident.closed_by_name ? `${incident.closed_by_name}, ${day(incident.closed_at)}` : undefined],
          ]}
          sections={[
            { title: "What happened", body: incident.description },
            { title: "Witnesses", body: incident.witnesses },
            { title: `Why it was escalated${incident.escalated_by_name ? ` (${incident.escalated_by_name})` : ""}`, body: incident.escalation_reason, tone: "warning" },
            { title: "Why leadership rejected the proposed action", body: incident.approval_status === "rejected" ? incident.approval_rejection_reason : "", tone: "danger" },
            { title: "Approval notes", body: incident.approval_notes },
            { title: "What the parent said", body: incident.parent_response },
            { title: "Action taken", body: incident.action_notes },
            { title: "Follow-up notes", body: incident.follow_up_notes },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {isReviewer && open && !incident.parent_notified && (
                <button type="button" disabled={notify.isPending} onClick={() => notify.mutate(incident.id)} className="btn btn-outline gap-1.5" title="Sends each parent account a notice asking them to contact the school. No details of the case are sent.">
                  <BellRing className="h-4 w-4" /> Tell the parent
                </button>
              )}
              {isReviewer && (incident.status === "reported" || incident.status === "escalated") && (
                <button type="button" disabled={investigate.isPending} onClick={() => investigate.mutate(incident.id)} className="btn btn-primary gap-1.5">
                  <Search className="h-4 w-4" /> Open an investigation
                </button>
              )}
              {isReviewer && incident.status === "resolved" && (
                <button type="button" disabled={close.isPending} onClick={() => close.mutate(incident.id)} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Close the case
                </button>
              )}
            </>
          }
        >
          {[investigate, notify, close].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}
          {isReviewer && open && !incident.parent_notified && (
            <p className="text-xs text-gray-500">Telling the parent sends a notice asking them to contact the school. No details of the case are sent.</p>
          )}

          {isLeadership && incident.requires_approval && (incident.approval_status === "pending" || incident.approval_status === "rejected") && open && (
            ownCase ? (
              <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-700">You reported this case, so another leader must decide the proposed action.</p>
            ) : (
              <>
                <ReasonPanel
                  title={incident.approval_status === "rejected" ? "Approve the proposed action after all" : "Approve the proposed action"}
                  description={`${incident.action_taken_name}. You cannot approve a case you reported.`}
                  label="What you checked"
                  placeholder="e.g. Read both statements and the duty teacher's note; the action fits the school policy."
                  context={{ key: "discipline.note", params: { kind: "approve", id: incident.id } }}
                  button="Approve"
                  busy={approve.isPending}
                  error={approve.error}
                  errorText="The approval could not be recorded."
                  onSubmit={(notes) => approve.mutate({ id: incident.id, notes })}
                />
                {awaitsApproval(incident) && (
                  <ReasonPanel
                    title="Reject the proposed action"
                    description="The committee must then decide the case again."
                    placeholder="e.g. A written warning is enough for a first incident of this kind."
                    context={{ key: "discipline.note", params: { kind: "reject", id: incident.id } }}
                    button="Reject"
                    danger
                    busy={reject.isPending}
                    error={reject.error}
                    errorText="The rejection could not be recorded."
                    onSubmit={(reason) => reject.mutate({ id: incident.id, reason })}
                  />
                )}
              </>
            )
          )}

          {isReviewer && incident.parent_notified && !incident.parent_response && incident.status !== "closed" && (
            <ReasonPanel
              title="Record what the parent said"
              label="The parent's response"
              placeholder="e.g. The mother came in on Tuesday, accepted the action and will talk to her son at home."
              context={{ key: "discipline.note", params: { kind: "parent_response", id: incident.id } }}
              button="Record it"
              busy={response.isPending}
              error={response.error}
              errorText="The response could not be recorded."
              onSubmit={(text) => response.mutate({ id: incident.id, response: text })}
            />
          )}

          {isReviewer && (incident.status === "investigating" || incident.status === "escalated") && (
            <ResolvePanel incident={incident} busy={resolve.isPending} error={resolve.error} onSubmit={(data) => resolve.mutate({ id: incident.id, data })} />
          )}

          {(incident.status === "reported" || incident.status === "investigating") && (
            <ReasonPanel
              title="Escalate the case"
              description="For a serious or repeated case that needs the committee and leadership now."
              placeholder="e.g. Third incident this term, and another learner was hurt."
              context={{ key: "discipline.note", params: { kind: "escalate", id: incident.id } }}
              button="Escalate"
              danger
              busy={escalate.isPending}
              error={escalate.error}
              errorText="The case could not be escalated."
              onSubmit={(reason) => escalate.mutate({ id: incident.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {reporting && (
        <IncidentForm
          learners={learners}
          categories={categories}
          sanctions={sanctions}
          onClose={() => setReporting(false)}
          onDone={(saved) => {
            setReporting(false);
            done(`Incident reported${saved.requires_approval ? "; the proposed action needs leadership approval" : ""}.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
